import styled, { keyframes } from "styled-components";
import { useState, useCallback, useMemo, memo } from "react";
import {
  RotateCcw,
  LockOpen,
  Lock,
  EllipsisVertical,
  ChevronDown,
  NotebookPen,
  MoveRight,
  StickyNote,
  AlertTriangle,
  Clock,
  HandCoins,
  HousePlus,
  BedDouble,
} from "lucide-react";
import { router, Head, usePoll } from "@inertiajs/react";

import { theme } from "../_kit/theme";
import BookingSourceIcon from "../_kit/BookingSourceIcon";
import {
  physicalStatusMeta,
  footerMeta,
  TRANSIT_OVERDUE_META,
  RESERVATION_META,
  RESERVED_BLOCK_META,
  PHYSICAL_STATUS_META,
} from "../_kit/status";
import { formatDate, timeCompact } from "../_kit/dateFormat";
import StatusBadge from "../_kit/StatusBadge";
import PageShell from "../_kit/PageShell";
import PropertyStatusBar from "../_kit/PropertyStatusBar";
import Legend from "../_kit/Legend";
import FoCommentsModal from "../_kit/FoCommentsModal";
import ConfirmDialog from "../_kit/ConfirmDialog";
import BlockRoomDialog from "../_kit/BlockRoomDialog";
import MoveBlockModal from "../_kit/MoveBlockModal";
import HouseUseDialog from "../_kit/HouseUseDialog";
import ReopenPickModal from "../_kit/ReopenPickModal";
import { reopenPolicyText } from "../_kit/reopenPolicy";
import BookingDrawer from "../_kit/BookingDrawer";
import { FoNotesButton, GuestNotesButton } from "../_kit/NotesButtons";
import { cleanComment } from "../_kit/text";
import { readDrawerLineId, useDrawerUrlSync } from "../_kit/useDrawerUrl";
import { useT } from "../_kit/i18n";

const PHYSICAL_STATUS_KEYS = [
  "vacant_clean",
  "vacant_dirty",
  "occupied_dirty",
  "maintenance",
];
const REOPEN_CRIT_KEYS = [
  "has_reservation",
  "stay_started",
  "no_pending_reopen",
  "room_still_reserved",
];

// Every guest activity present on the room for the date — each rendered as its own row with the
// guest's name attached, so it's never ambiguous which guest a name belongs to.
const ACTIVITIES = [
  { key: "check_in_info", label: "CI" },
  { key: "stay_info", label: "Stay" },
  { key: "check_out_info", label: "CO" },
  { key: "early_checkout_info", label: "Early CO" },
];

// Compact ("Padat") view: a dense, old-rooms-grid-style scan so the FO sees every room at once and
// decides fast; Detailed keeps the rich per-room cards. Choice persists per device.
const DENSITY_KEY = "frontdesk_rack_density";
const readDensity = () => {
  if (typeof localStorage === "undefined") {
    return "compact";
  }
  return localStorage.getItem(DENSITY_KEY) === "detailed"
    ? "detailed"
    : "compact";
};

const Header = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 1rem;
`;

const Title = styled.h1`
  margin: 0;
  font-size: 1.4rem;
`;

const Subtitle = styled.p`
  margin: 0.25rem 0 0;
  color: ${theme.muted};
  font-size: 0.9rem;
`;

const Section = styled.section`
  margin-top: 1.25rem;
`;

const SectionTitle = styled.h2`
  margin: 0 0 0.5rem;
  font-size: 0.95rem;
  color: ${theme.ink};
`;

const FloorTitle = styled.h3`
  margin: 0.75rem 0 0.4rem;
  font-size: 0.82rem;
  font-weight: 600;
  color: ${theme.muted};
  text-transform: uppercase;
  letter-spacing: 0.04em;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  gap: 0.75rem;
`;

const Card = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 0.55rem;
  padding: 0.75rem;
  background: ${(props) => props.$tint};
  border: 1px solid ${theme.border};
  border-radius: 12px;
`;

const Kebab = styled.button`
  position: absolute;
  top: 0.5rem;
  right: 0.5rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.75rem;
  height: 1.75rem;
  border: 1px solid ${theme.border};
  border-radius: 8px;
  background: #ffffff;
  color: ${theme.inkSoft};
  cursor: pointer;
  &:hover {
    background: ${theme.goldSoft};
    border-color: ${theme.gold};
  }
`;

const CardTop = styled.div`
  display: flex;
  align-items: baseline;
  gap: 0.5rem;
  padding-right: 1.75rem;
`;

const RoomCode = styled.span`
  font-weight: 800;
  font-size: 25px;
  line-height: 1.1;
  color: ${theme.ink};
`;

const RoomType = styled.span`
  font-size: 0.75rem;
  color: ${theme.muted};
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
`;

const Caption = styled.span`
  font-size: 0.75rem;
  font-weight: 700;
  color: ${theme.muted};
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

const BadgeRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.3rem;
`;

const ActivityRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.4rem;
`;

const ActivityChip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.28rem;
  flex: 0 0 auto;
  padding: 0.16rem 0.45rem;
  border-radius: 6px;
  font-size: 0.74rem;
  font-weight: 800;
  color: ${(props) => props.$fg};
  background: ${(props) => props.$bg};
`;

// Holds the guest name and the markers that belong TO the name, so they stay glued to it instead of
// drifting to the end of the row. Takes the flex the name itself used to, so everything further along
// the row (the late tag, the attention mark) stays exactly where it was.
const GuestCell = styled.span`
  align-items: center;
  display: inline-flex;
  flex: 1 1 auto;
  gap: 0.3rem;
  min-width: 0;
`;

// One place decides the badge's size and its two translated labels, so the five rack sites cannot
// drift apart. The key itself always comes from the server.
const sourceBadge = (sourceKey, t, size) => (
  <BookingSourceIcon
    sourceKey={sourceKey}
    size={size}
    label={sourceKey === "walkin" ? t("source_walkin") : t("source_unknown")}
  />
);

// Spelled out, never abbreviated. A rare marker nobody sees often enough to learn as an icon, on a
// row where getting it wrong means arguing about occupancy with head office.
const HouseUseMark = styled.span`
  background: #eaf1fb;
  border-radius: 5px;
  color: #2f4a70;
  flex: 0 0 auto;
  font-size: 0.66rem;
  font-weight: 800;
  letter-spacing: 0.01em;
  padding: 0.1rem 0.3rem;
  white-space: nowrap;
`;

/* Beds the room needs TONIGHT, as a count against a bed icon.
 *
 * Per date, not per stay: a bed is inventoried by night, so a three-night booking with one bed needs
 * one bed each night rather than three. The housekeeper reading this grid is carrying beds up the
 * stairs for the date on screen.
 *
 * Blue rather than amber or red — it is a thing to do, not a thing that is wrong, and the row already
 * spends amber on an unpaid booking.
 */
const ExtraBedMark = styled.span`
  align-items: center;
  background: #e8f0fe;
  border-radius: 5px;
  color: #1a4480;
  display: inline-flex;
  flex: 0 0 auto;
  font-size: 0.66rem;
  font-weight: 800;
  gap: 0.12rem;
  padding: 0.1rem 0.26rem;
  white-space: nowrap;
`;

const ExtraBedBadge = (props) => {
  const { t } = useT();
  const count = Number(props.count || 0);
  if (!count) return null;

  const label = t("extra_bed_mark").replace("%{count}", count);
  return (
    <ExtraBedMark title={label} aria-label={label}>
      <BedDouble size={11} aria-hidden="true" />+{count}
    </ExtraBedMark>
  );
};

const ActivityGuest = styled.span`
  flex: 0 1 auto;
  min-width: 0;
  font-size: 0.83rem;
  color: ${theme.ink700};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const NotesRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.3rem;
`;

const OtaBox = styled.div`
  padding: 0.4rem 0.55rem;
  border: 1px solid #f0dfc0;
  border-radius: 8px;
  background: #fff8ec;
  font-size: 0.78rem;
  color: ${theme.ink700};
  white-space: pre-wrap;
  word-break: break-word;
`;

const OtaLabel = styled.div`
  font-size: 0.75rem;
  font-weight: 700;
  color: ${theme.muted};
  text-transform: uppercase;
  letter-spacing: 0.04em;
  margin-bottom: 0.15rem;
`;

const ActivityList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
`;

// One boxed block per guest activity (CI / Stay / CO / Early CO) so its guest, notes and its OWN
// "Buka booking" link stay together. The block is tinted by the activity colour (green arrive / blue
// stay / orange leave / amber plain-reservation) with a left accent — colour = kind of movement.
const ActivityBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  padding: 0.5rem 0.55rem;
  border: 1px solid ${theme.border};
  border-left: 3px solid ${(props) => props.$accent || theme.border};
  border-radius: 8px;
  background: ${(props) => props.$tint || "#fbfaf7"};
  cursor: ${(props) => (props.$clickable ? "pointer" : "default")};
  transition: box-shadow 0.15s;
  &:hover {
    box-shadow: ${(props) =>
      props.$clickable ? "0 2px 8px -3px rgba(26, 26, 26, 0.22)" : "none"};
  }
`;

const OverdueTag = styled.span`
  align-self: flex-start;
  padding: 0.12rem 0.45rem;
  border-radius: 999px;
  font-size: 0.72rem;
  font-weight: 700;
  color: #92400e;
  background: #fef3c7;
`;

// Shown on a CO card after a same-day checkout: the guest already left, so the card stays for
// reference but reads "done" instead of "still due to leave".
const DoneTag = styled.span`
  align-self: flex-start;
  padding: 0.12rem 0.45rem;
  border-radius: 999px;
  font-size: 0.72rem;
  font-weight: 700;
  color: #166534;
  background: #dcfce7;
`;

const BlockReason = styled.div`
  color: #4a4f63;
  font-size: 0.76rem;
  line-height: 1.35;
  margin-top: 0.15rem;
  strong {
    color: #2f3242;
  }
`;

const BlockDates = styled.div`
  font-size: 0.78rem;
  color: ${theme.ink700};
`;

const ActivityLink = styled.button`
  align-self: flex-start;
  padding: 0;
  border: 0;
  background: transparent;
  font-size: 0.78rem;
  font-weight: 600;
  color: ${theme.goldDark};
  cursor: pointer;
  &:hover {
    text-decoration: underline;
  }
`;

const Backdrop = styled.div`
  position: fixed;
  inset: 0;
  z-index: 40;
`;

const Popover = styled.div`
  position: absolute;
  top: 2.25rem;
  right: 0.5rem;
  z-index: 50;
  width: 230px;
  padding: 0.35rem;
  background: #ffffff;
  border: 1px solid ${theme.border};
  border-radius: 10px;
  box-shadow: 0 14px 34px -12px rgba(26, 26, 26, 0.35);
`;

const PopLabel = styled.div`
  padding: 0.35rem 0.5rem 0.2rem;
  font-size: 0.74rem;
  font-weight: 700;
  color: ${theme.muted};
  text-transform: uppercase;
  letter-spacing: 0.04em;
`;

const PopItem = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  width: 100%;
  padding: 0.4rem 0.5rem;
  border: 0;
  border-radius: 8px;
  background: transparent;
  font-size: 0.82rem;
  color: ${theme.ink};
  text-align: left;
  cursor: pointer;
  &:hover:not(:disabled) {
    background: ${theme.goldSoft};
  }
  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }
`;

const Dot = styled.span`
  width: 0.7rem;
  height: 0.7rem;
  border-radius: 3px;
  flex: 0 0 auto;
  background: ${(props) => props.$color};
`;

const Reasons = styled.div`
  padding: 0.15rem 0.5rem 0.4rem;
`;

const ReasonsTitle = styled.div`
  font-size: 0.74rem;
  font-weight: 700;
  color: ${theme.muted};
  margin-bottom: 0.15rem;
`;

const Reason = styled.div`
  font-size: 0.75rem;
  color: ${(props) => (props.$ok ? "#166534" : "#991b1b")};
  line-height: 1.35;
`;

const Empty = styled.div`
  padding: 3rem 1rem;
  text-align: center;
  color: ${theme.muted};
  background: #ffffff;
  border: 1px dashed ${theme.border};
  border-radius: 12px;
`;

const ViewToggle = styled.div`
  display: inline-flex;
  border: 1px solid ${theme.border};
  border-radius: 999px;
  overflow: hidden;
  align-self: center;
`;

const ViewButton = styled.button`
  border: 0;
  padding: 0.35rem 0.75rem;
  font-size: 0.78rem;
  font-weight: 700;
  cursor: pointer;
  color: ${(props) => (props.$active ? theme.ink : theme.muted)};
  background: ${(props) => (props.$active ? theme.goldSoft : "#ffffff")};
`;

const reservationBadges = (room) => {
  const list = [];
  const transitCount = room.transit_active_count || 0;
  if (room.nightly_reservation_status === "reserved") {
    list.push(RESERVATION_META.reserved);
  } else if (transitCount > 0) {
    list.push({ ...RESERVATION_META.transit, suffix: ` ${transitCount}x` });
  }
  if (room.blocked) {
    list.push(RESERVATION_META.blocked);
  }
  if (list.length === 0) {
    list.push(RESERVATION_META.available);
  }
  return list;
};

// Memoized so a state change in the parent (e.g. opening the comments modal) doesn't re-render every
// cell. Popover + OTA-note open state is LOCAL to each card, so toggling one card touches only it.
const RoomCard = memo((props) => {
  const { t, lang } = useT();
  const room = props.room;
  const [menuOpen, setMenuOpen] = useState(false);
  const [otaKey, setOtaKey] = useState(null);

  const pMeta = physicalStatusMeta(room.status);
  const tint = room.status === "vacant_dirty" ? "#fffaf0" : "#ffffff";

  const activities = ACTIVITIES.filter((activity) => room[activity.key]).map(
    (activity) => {
      const info = room[activity.key];
      const meta = footerMeta(activity.label);
      const Icon = meta.Icon;
      const key = `${room.id}:${activity.key}`;
      const hasComment = info.comment && info.comment.trim().length > 0;
      return (
        <ActivityBlock
          key={activity.key}
          $tint={meta.tint}
          $accent={meta.accent}
          $clickable={Boolean(info.line_id)}
          role={info.line_id ? "button" : undefined}
          onClick={
            info.line_id ? () => props.onOpenBooking(info.line_id) : undefined
          }
        >
          <ActivityRow>
            <ActivityChip $bg={meta.bg} $fg={meta.fg}>
              <Icon size={12} aria-hidden="true" />
              {activity.label}
            </ActivityChip>
            <GuestCell>
              {sourceBadge(info.booking_source_key, t, 16)}
              <ActivityGuest title={info.guest_name}>
                {info.guest_name || "—"}
              </ActivityGuest>
              {info.house_use ? (
                <HouseUseMark title={t("house_use_badge")}>
                  {t("house_use_badge")}
                </HouseUseMark>
              ) : null}
              {info.rate_paid === false && !info.house_use ? (
                <UnpaidMark
                  title={t("unpaid_mark")}
                  aria-label={t("unpaid_mark")}
                >
                  <HandCoins size={12} aria-hidden="true" />
                </UnpaidMark>
              ) : null}
              <ExtraBedBadge count={info.extra_bed_count} />
            </GuestCell>
          </ActivityRow>
          {info.status === "checked_out" ? (
            <DoneTag>
              ✓ {t("co_done")}
              {info.checked_out_at ? ` · ${info.checked_out_at.slice(11)}` : ""}
            </DoneTag>
          ) : null}
          {/* The hour the guest has PAID to stay until — the number housekeeping and the next
              arrival both plan around, and the one the door card must be re-encoded to. */}
          {info.late_checkout_until ? (
            <LateTag>
              <Clock size={11} />
              {t("late_until")} <strong>{info.late_checkout_until}</strong>
            </LateTag>
          ) : null}
          <NotesRow onClick={(event) => event.stopPropagation()}>
            {info.line_id ? (
              <FoNotesButton
                counts={props.notesCounts?.[info.line_id]}
                onClick={() =>
                  props.onOpenComments({
                    id: info.line_id,
                    room_code: room.code,
                    guest_name: info.guest_name,
                  })
                }
              />
            ) : null}
            {hasComment ? (
              <GuestNotesButton
                hasNote
                active={otaKey === key}
                onClick={() => setOtaKey(otaKey === key ? null : key)}
              />
            ) : null}
          </NotesRow>
          {otaKey === key ? (
            <OtaBox>
              <OtaLabel>{t("ota_note_title")}</OtaLabel>
              {cleanComment(info.comment)}
            </OtaBox>
          ) : null}
          {info.line_id ? (
            <ActivityLink
              type="button"
              onClick={() => props.onOpenBooking(info.line_id)}
            >
              {t("open_booking")} →
            </ActivityLink>
          ) : null}
        </ActivityBlock>
      );
    },
  );

  // Transit (day-use) stays are an ARRAY, not one of the four singular buckets: a room can hold
  // several in one day, and each is its own guest with its own hours. Rendered after the nightly
  // activities so the day's overnight story still reads first.
  const transitBlocks = (room.transit_infos || []).map((info) => {
    const meta = info.overdue ? TRANSIT_OVERDUE_META : footerMeta("Transit");
    const Icon = meta.Icon;
    return (
      <ActivityBlock
        key={`transit:${info.line_id}`}
        $tint={meta.tint}
        $accent={meta.accent}
        $clickable={Boolean(info.line_id)}
        role={info.line_id ? "button" : undefined}
        onClick={
          info.line_id ? () => props.onOpenBooking(info.line_id) : undefined
        }
      >
        <ActivityRow>
          <ActivityChip $bg={meta.bg} $fg={meta.fg}>
            <Icon size={12} aria-hidden="true" />
            {transitWindow(info, t).label}
          </ActivityChip>
          <GuestCell>
            {sourceBadge(info.booking_source_key, t, 16)}
            <ActivityGuest title={info.guest_name}>
              {info.guest_name || "—"}
            </ActivityGuest>
            {info.house_use ? (
              <HouseUseMark title={t("house_use_badge")}>
                {t("house_use_badge")}
              </HouseUseMark>
            ) : null}
            {info.rate_paid === false && !info.house_use ? (
              <UnpaidMark
                title={t("unpaid_mark")}
                aria-label={t("unpaid_mark")}
              >
                <HandCoins size={12} aria-hidden="true" />
              </UnpaidMark>
            ) : null}
            <ExtraBedBadge count={info.extra_bed_count} />
          </GuestCell>
        </ActivityRow>
        <DoneTag>
          {t("transit")}
          {info.transit_duration_hours
            ? ` ${info.transit_duration_hours} ${t("transit_hours")}`
            : ""}
          {info.overdue ? ` · ${t("transit_overdue")}` : ""}
        </DoneTag>
        {transitWindow(info, t).note ? (
          <CrossDayTag>{transitWindow(info, t).note}</CrossDayTag>
        ) : null}
        {info.line_id ? (
          <ActivityLink
            type="button"
            onClick={() => props.onOpenBooking(info.line_id)}
          >
            {t("open_booking")} →
          </ActivityLink>
        ) : null}
      </ActivityBlock>
    );
  });

  return (
    <Card $tint={tint}>
      <Kebab
        type="button"
        aria-label={t("actions")}
        onClick={() => setMenuOpen(!menuOpen)}
      >
        <EllipsisVertical size={16} />
      </Kebab>
      {menuOpen ? (
        <>
          <Backdrop onClick={() => setMenuOpen(false)} />
          <Popover>
            <PopLabel>{t("change_status")}</PopLabel>
            {PHYSICAL_STATUS_KEYS.map((key) => {
              const meta = PHYSICAL_STATUS_META[key];
              const current = room.status === key;
              return (
                <PopItem
                  key={key}
                  type="button"
                  disabled={current}
                  title={current ? t("status_current_hint") : ""}
                  onClick={() => {
                    setMenuOpen(false);
                    props.onChangeStatus(room, key);
                  }}
                >
                  <Dot $color={meta.bg} />
                  {t(meta.labelKey)}
                </PopItem>
              );
            })}

            <PopLabel>{t("actions")}</PopLabel>
            <PopItem
              type="button"
              onClick={() => {
                setMenuOpen(false);
                props.onToggleBlock(room);
              }}
            >
              {room.blocked ? <LockOpen size={14} /> : <Lock size={14} />}
              {room.blocked ? t("unblock_room") : t("block_room")}
            </PopItem>
            {room.blocked ? (
              <PopItem
                type="button"
                onClick={() => {
                  setMenuOpen(false);
                  props.onEditBlockReason(room);
                }}
              >
                <NotebookPen size={14} />
                {t("edit_block_reason")}
              </PopItem>
            ) : null}
            {room.block_movable ? (
              <PopItem
                type="button"
                onClick={() => {
                  setMenuOpen(false);
                  props.onMoveBlock(room);
                }}
              >
                <MoveRight size={14} />
                {t("move_block")}
              </PopItem>
            ) : null}
            {/* Only on a MOVABLE block: block_movable already means an operator block with no
             * reservation on it, which is exactly the room a house-use stay can be written onto.
             * A close-out block or a night that already holds a booking must not offer this. */}
            {room.block_movable ? (
              <PopItem
                type="button"
                onClick={() => {
                  setMenuOpen(false);
                  props.onMarkHouseUse(room);
                }}
              >
                <HousePlus size={14} />
                {t("house_use_menu")}
              </PopItem>
            ) : null}
            <PopItem
              type="button"
              disabled={!room.reopen_eligible}
              onClick={() => {
                setMenuOpen(false);
                props.onRequestReopen(room);
              }}
            >
              <RotateCcw size={14} />
              {t("request_reopen")}
            </PopItem>
            {room.reopen_criteria ? (
              <Reasons>
                {room.reopen_eligible ? (
                  <Reason $ok>✓ {t("reopen_ready")}</Reason>
                ) : (
                  <>
                    <ReasonsTitle>{t("reopen_why")}</ReasonsTitle>
                    {REOPEN_CRIT_KEYS.filter(
                      (key) => room.reopen_criteria[key] === false,
                    ).map((key) => (
                      <Reason key={key} $ok={false}>
                        ✗ {t(`crit_${key}`)}
                      </Reason>
                    ))}
                  </>
                )}
              </Reasons>
            ) : null}
          </Popover>
        </>
      ) : null}

      <CardTop>
        <RoomCode>{room.code}</RoomCode>
        <RoomType>{room.room_type_name}</RoomType>
      </CardTop>

      <Field>
        <Caption>{t("caption_room")}</Caption>
        <div>
          <StatusBadge meta={pMeta} variant="soft" block />
        </div>
      </Field>

      <Field>
        <Caption>{t("caption_date")}</Caption>
        <BadgeRow>
          {reservationBadges(room).map((meta) => (
            <StatusBadge key={meta.labelKey} meta={meta} variant="outline" />
          ))}
        </BadgeRow>
        {/* Why the room is locked, so the next shift does not have to ask. */}
        {room.blocked && (room.block_reason || room.block_note) ? (
          <BlockReason>
            {room.block_reason ? (
              <strong>{t(`block_reason_${room.block_reason}`)}</strong>
            ) : null}
            {room.block_note ? <span> {room.block_note}</span> : null}
          </BlockReason>
        ) : null}
      </Field>

      {activities.length > 0 ? (
        <Field>
          <Caption>{t("guest")}</Caption>
          <ActivityList>
            {activities}
            {transitBlocks}
          </ActivityList>
        </Field>
      ) : null}

      {/* No nightly activity bucket. The room may still hold a reservation that never checked in,
          transit stays, or both — a transit-only room has no `current_room_booking_line_id` at all,
          so this branch cannot key on it alone or the day-use guest disappears from the rack. */}
      {activities.length === 0 &&
      (room.current_room_booking_line_id || transitBlocks.length > 0) ? (
        <Field>
          <Caption>{t("guest")}</Caption>
          <ActivityList>
            {room.current_room_booking_line_id ? (
              <ActivityBlock
                $tint={RESERVED_BLOCK_META.tint}
                $accent={RESERVED_BLOCK_META.accent}
                $clickable
                role="button"
                onClick={() =>
                  props.onOpenBooking(room.current_room_booking_line_id)
                }
              >
                <ActivityRow>
                  <StatusBadge
                    meta={RESERVATION_META.reserved}
                    variant="outline"
                  />
                  <GuestCell>
                    {sourceBadge(room.booking_source_key, t, 16)}
                    <ActivityGuest title={room.guest_name}>
                      {room.guest_name || "—"}
                    </ActivityGuest>
                    {room.house_use ? (
                      <HouseUseMark title={t("house_use_badge")}>
                        {t("house_use_badge")}
                      </HouseUseMark>
                    ) : null}
                    {room.rate_paid === false && !room.house_use ? (
                      <UnpaidMark
                        title={t("unpaid_mark")}
                        aria-label={t("unpaid_mark")}
                      >
                        <HandCoins size={12} aria-hidden="true" />
                      </UnpaidMark>
                    ) : null}
                    <ExtraBedBadge count={room.extra_bed_count} />
                  </GuestCell>
                </ActivityRow>
                {room.line_check_in_date ? (
                  <BlockDates>
                    {/* line_check_in/out_date are raw datetime strings — take the ISO date part */}
                    {formatDate(
                      room.line_check_in_date.slice(0, 10),
                      lang,
                      false,
                    )}{" "}
                    →{" "}
                    {formatDate(
                      (room.line_check_out_date || "").slice(0, 10),
                      lang,
                      false,
                    )}
                  </BlockDates>
                ) : null}
                {room.line_status === "reserved" &&
                room.line_check_in_date &&
                room.line_check_in_date.slice(0, 10) < props.date ? (
                  <OverdueTag>{t("overdue_not_checked_in")}</OverdueTag>
                ) : null}
                <NotesRow>
                  <FoNotesButton
                    counts={
                      props.notesCounts?.[room.current_room_booking_line_id]
                    }
                    onClick={() =>
                      props.onOpenComments({
                        id: room.current_room_booking_line_id,
                        room_code: room.code,
                        guest_name: room.guest_name,
                      })
                    }
                  />
                </NotesRow>
                <ActivityLink
                  type="button"
                  onClick={() =>
                    props.onOpenBooking(room.current_room_booking_line_id)
                  }
                >
                  {t("open_booking")} →
                </ActivityLink>
              </ActivityBlock>
            ) : null}
            {transitBlocks}
          </ActivityList>
        </Field>
      ) : null}
    </Card>
  );
});
RoomCard.displayName = "RoomCard";

// --- Compact ("Padat") matrix -----------------------------------------------------------------
// The old rooms-grid density, enhanced: a bordered, grid-lined matrix (crisp 1px lines via a 1px gap
// over a border-coloured background) of fixed-width cells. Each cell shows the room's physical status
// (left colour strip + soft tint + label), and one row PER guest movement (CI / Stay / CO / Early)
// with that guest's name — each row clickable straight to its own booking. So a same-day turnover
// reads as two distinct, separately-openable rows ("CO Budi" leaving, "CI Andi" arriving) instead of
// one ambiguous tile. The room number is the row-action trigger (styled as a button with a caret): it
// opens the same quick actions as the Detailed view — change physical status, block / unblock, propose
// reopen.
// No overflow:hidden here — the row-action popover is absolutely positioned inside a cell and must be
// free to overflow the matrix; the 1px gap over a border-coloured background still draws the gridlines.
const CompactGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(210px, 1fr));
  gap: 2px;
  background: ${theme.border};
  border: 1px solid ${theme.border};
`;

// Two glow states so the two rooms the FO acts on jump out of the matrix: Vacant Dirty (needs
// housekeeping, orange) and reservation Available (free to sell, green). Vacant Dirty wins when a room
// is both. Also applied to the Vacant-Dirty status chip.
const dirtyGlow = keyframes`
  0%,
  100% {
    box-shadow: 0 0 0 0 rgba(253, 126, 20, 0);
  }
  50% {
    box-shadow: 0 0 9px 1px rgba(253, 126, 20, 0.6);
  }
`;

const availableGlow = keyframes`
  0%,
  100% {
    box-shadow: 0 0 0 0 rgba(40, 167, 69, 0);
  }
  50% {
    box-shadow: 0 0 9px 1px rgba(40, 167, 69, 0.5);
  }
`;

// Cell tint per glow kind; everything else stays white.
const CELL_TINT = { dirty: "#fff3e6", available: "#eafaf1" };

const CCard = styled.div`
  position: relative;
  /* While its menu is open, lift the card above sibling cards so the downward popover is never covered
     by a later cell (glowing cells sit at z-index 1, and DOM order would otherwise let a later one win). */
  z-index: ${(props) => (props.$menuOpen ? 100 : props.$glow ? 1 : "auto")};
  display: flex;
  flex-direction: column;
  gap: 0.34rem;
  min-height: 104px;
  padding: 0.5rem 0.6rem;
  background: ${(props) => CELL_TINT[props.$glow] || "#ffffff"};
  animation: ${(props) =>
    props.$glow === "dirty"
      ? dirtyGlow
      : props.$glow === "available"
        ? availableGlow
        : "none"}
    1.6s ease-in-out infinite;
`;

const CTop = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.4rem;
`;

// The room number IS the row-action trigger — styled as a button (border + caret + hover) so the FO
// reads it as clickable, not just a label.
const CCode = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.16rem 0.34rem 0.16rem 0.48rem;
  border: 1px solid ${theme.border};
  border-radius: 7px;
  background: #ffffff;
  font-weight: 800;
  font-size: 1.28rem;
  line-height: 1;
  color: ${theme.ink};
  cursor: pointer;
  transition:
    background 0.12s,
    border-color 0.12s;
  &:hover {
    background: ${theme.goldSoft};
    border-color: ${theme.gold};
  }
  svg {
    color: ${theme.muted};
  }
`;

const CBlockReason = styled.div`
  color: #4a4f63;
  font-size: 0.72rem;
  line-height: 1.3;
  margin-top: 0.15rem;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const CStatus = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.12rem 0.44rem;
  border-radius: 6px;
  font-size: 0.78rem;
  font-weight: 800;
  color: #ffffff;
  background: ${(props) => props.$bg};
  white-space: nowrap;
  animation: ${(props) => (props.$glow ? dirtyGlow : "none")} 1.6s ease-in-out
    infinite;
`;

// Room type CODE (left) + reservation status (right) share one row, so every status reads down the
// right edge of the cell.
const CTypeRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.4rem;
`;

const CType = styled.span`
  font-size: 0.86rem;
  font-weight: 700;
  color: ${theme.ink700};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const CPillGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 0.3rem;
  flex-wrap: wrap;
  justify-content: flex-end;
`;

const CNotes = styled.div`
  display: flex;
  align-items: center;
  gap: 0.35rem;
`;

// FO / Guest note indicators. Strong (coloured, filled) when a note exists so the FO instantly sees
// it; faint outline when absent, so the two slots stay in a fixed, learnable spot.
const CNoteChip = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.1rem 0.4rem;
  border-radius: 999px;
  font-size: 0.72rem;
  font-weight: 800;
  cursor: ${(props) => (props.$on && props.$clickable ? "pointer" : "default")};
  color: ${(props) => (props.$on ? "#ffffff" : theme.muted)};
  background: ${(props) => (props.$on ? props.$accent : "transparent")};
  border: 1px solid ${(props) => (props.$on ? props.$accent : theme.border)};
  opacity: ${(props) => (props.$on ? 1 : 0.55)};
`;

const CActs = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.22rem;
`;

const CAct = styled.div`
  display: flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.2rem 0.38rem;
  border-radius: 6px;
  background: ${(props) => props.$bg};
  cursor: ${(props) => (props.$clickable ? "pointer" : "default")};
  &:hover {
    box-shadow: ${(props) =>
      props.$clickable ? "inset 0 0 0 1px rgba(26, 26, 26, 0.18)" : "none"};
  }
`;

const CActLabel = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  flex: 0 0 auto;
  font-size: 0.72rem;
  font-weight: 800;
  letter-spacing: 0.02em;
  color: ${(props) => props.$fg};
`;

const CActGuest = styled.span`
  flex: 0 1 auto;
  min-width: 0;
  font-size: 0.86rem;
  color: ${theme.ink700};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

// The room charge is still owed. Sits beside the guest name so the FO sees it while scanning the
// board, without opening the drawer — the same place and the same "cursor: help" affordance as the
// email-attention marker below. Amber, matching the "Booking belum lunas" badge on Beranda, because
// one meaning wearing two colours is how a front desk stops trusting either.
const UnpaidMark = styled.span`
  align-items: center;
  color: #b45309;
  cursor: help;
  display: inline-flex;
  flex: 0 0 auto;
`;

// Needs-attention marker on a guest row (OTA email + online deposit paid).
const CAttn = styled.span`
  display: inline-flex;
  align-items: center;
  flex: 0 0 auto;
  color: #b45309;
  cursor: help;
`;

// The paid-until hour, on the row of the guest who bought it.
const CLate = styled.span`
  align-items: center;
  background: #fdf3e3;
  border-radius: 999px;
  color: #8a5a00;
  cursor: help;
  display: inline-flex;
  flex: 0 0 auto;
  font-size: 0.66rem;
  font-weight: 800;
  gap: 0.12rem;
  padding: 0.02rem 0.3rem;
`;

// Which side of midnight this slot's hours fall on. Without it the same "21:00-02:00" appears on two
// consecutive days and neither reading is checkable.
const CCross = styled.span`
  background: #f5f3ff;
  border-radius: 999px;
  color: #6d28d9;
  cursor: help;
  flex: 0 0 auto;
  font-size: 0.62rem;
  font-weight: 800;
  max-width: 8.5rem;
  overflow: hidden;
  padding: 0.02rem 0.3rem;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const CPill = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.1rem 0.42rem;
  border-radius: 999px;
  font-size: 0.74rem;
  font-weight: 800;
  color: ${(props) => (props.$solid ? "#ffffff" : props.$c)};
  border: 1px solid ${(props) => props.$solid || props.$c};
  background: ${(props) => props.$solid || "#ffffff"};
  white-space: nowrap;
`;

// Row-action popover for the compact cell. Anchored to the LEFT (under the room-number trigger) so it
// grows rightward and never clips the left-most column (101) against the page edge.
const CPopover = styled.div`
  position: absolute;
  top: 2.5rem;
  left: 0.5rem;
  z-index: 50;
  width: 220px;
  max-width: calc(100vw - 2rem);
  padding: 0.35rem;
  background: #ffffff;
  border: 1px solid ${theme.border};
  border-radius: 10px;
  box-shadow: 0 14px 34px -12px rgba(26, 26, 26, 0.35);
`;

// Build one clickable row per guest movement on the room. A reserved room with no movement bucket yet
// (an arrival not checked in) still gets a single "RSV" row so the arriving guest is openable.
const compactActs = (room, t) => {
  const acts = ACTIVITIES.filter((activity) => room[activity.key]).map(
    (activity) => {
      const info = room[activity.key];
      const meta = footerMeta(activity.label);
      return {
        key: activity.key,
        label: activity.label,
        Icon: meta.Icon,
        bg: meta.bg,
        fg: meta.fg,
        guest: info.guest_name,
        lineId: info.line_id,
        attention: info.email_attention,
        ratePaid: info.rate_paid,
        houseUse: info.house_use,
        sourceKey: info.booking_source_key,
        // The hour this guest has PAID to stay until. Housekeeping and the next arrival both plan
        // around it, and it used to live only inside the booking drawer.
        lateUntil: info.late_checkout_until,
        extraBeds: info.extra_bed_count,
      };
    },
  );
  if (acts.length === 0 && room.current_room_booking_line_id) {
    acts.push({
      key: "rsv",
      label: "RSV",
      Icon: RESERVATION_META.reserved.Icon,
      bg: RESERVED_BLOCK_META.tint,
      fg: RESERVATION_META.reserved.color,
      guest: room.guest_name,
      lineId: room.current_room_booking_line_id,
      ratePaid: room.rate_paid,
      houseUse: room.house_use,
      sourceKey: room.booking_source_key,
      extraBeds: room.extra_bed_count,
    });
  }
  // Transit stays are appended, never folded into a bucket: several can share the room-date, and the
  // hours ARE the label — "Transit" alone would not tell the FO whether the room is free right now.
  (room.transit_infos || []).forEach((info) => {
    const meta = info.overdue ? TRANSIT_OVERDUE_META : footerMeta("Transit");
    acts.push({
      key: `transit:${info.line_id}`,
      label: transitWindow(info, t).label,
      note: transitWindow(info, t).note,
      Icon: meta.Icon,
      bg: meta.bg,
      fg: meta.fg,
      guest: info.guest_name,
      lineId: info.line_id,
      ratePaid: info.rate_paid,
      houseUse: info.house_use,
      sourceKey: info.booking_source_key,
      extraBeds: info.extra_bed_count,
    });
  });
  return acts;
};

// Single date-status per room, matching the old grid + Legend: Reserved (or Available when neither),
// plus a Blocked marker when the date is also blocked. Reserved takes precedence over Available.
// A slot running past midnight is listed on BOTH days, so the hours alone are ambiguous: "21:00–02:00"
// read identically on the day the guest arrives and on the day they are already in the room. These say
// which side of midnight the viewed date is on, and show only the hours that fall on it.
// The cell is dense, so the hours carry the day-part ICON only — enough to tell 09:00 from 21:00 at a
// glance without wrapping the card. The full "(9 PM / Malam)" reading lives where the hour is chosen
// or confirmed.
const transitWindow = (info, t) => {
  const from = timeCompact(info.transit_from);
  const to = timeCompact(info.transit_to);

  if (!info.transit_crosses_midnight) {
    return { label: `${from}–${to}`, note: null };
  }
  if (info.starts_on_date) {
    return {
      label: `${from}–${to}⁺¹`,
      note: `${t("transit_continues")} ${to}`,
    };
  }
  return {
    label: `${timeCompact("00:00")}–${to}`,
    note: `${t("transit_continued")} ${from}`,
  };
};

const LateTag = styled.div`
  align-items: center;
  background: #fdf3e3;
  border-radius: 7px;
  color: #8a5a00;
  display: flex;
  font-size: 0.7rem;
  font-weight: 800;
  gap: 0.22rem;
  margin-top: 0.2rem;
  padding: 0.12rem 0.4rem;
`;

const CrossDayTag = styled.div`
  background: #f5f3ff;
  border-radius: 7px;
  color: #6d28d9;
  font-size: 0.68rem;
  font-weight: 800;
  margin-top: 0.2rem;
  padding: 0.12rem 0.4rem;
`;

const dateStatusMetas = (room) => {
  const metas = [];
  const transitCount = room.transit_active_count || 0;
  /* The DATE axis, which is not the same question as occupancy. `reservation_status` says whether
   * anyone is IN the room; this says whether the NIGHT is sold. A transit stay sells hours, so it
   * gets its own state and a count — one room can hold several day-use stays in a day.
   */
  if (room.nightly_reservation_status === "reserved") {
    metas.push(RESERVATION_META.reserved);
  } else if (transitCount > 0) {
    metas.push({ ...RESERVATION_META.transit, suffix: ` ${transitCount}x` });
  } else if (!room.blocked) {
    metas.push(RESERVATION_META.available);
  }
  if (room.blocked) {
    metas.push(RESERVATION_META.blocked);
  }
  return metas;
};

const CompactCard = memo((props) => {
  const { t } = useT();
  const room = props.room;
  const [menuOpen, setMenuOpen] = useState(false);
  const pMeta = physicalStatusMeta(room.status);
  const PhysicalIcon = pMeta.Icon;
  const acts = compactActs(room, t);
  const dateStatuses = dateStatusMetas(room);
  const typeLabel = room.room_type_code || room.room_type_name || "—";
  const isAvailable =
    room.nightly_reservation_status !== "reserved" &&
    !room.blocked &&
    !room.transit_active_count;
  const glowKind =
    room.status === "vacant_dirty" ? "dirty" : isAvailable ? "available" : null;

  const primaryLineId = acts.map((act) => act.lineId).find(Boolean) || null;
  const primaryGuest = acts.find((act) => act.lineId)?.guest || room.guest_name;

  let foTotal = 0;
  let foUnchecked = 0;
  acts.forEach((act) => {
    const count = act.lineId ? props.notesCounts?.[act.lineId] : null;
    if (count) {
      foTotal += count.total || 0;
      foUnchecked += count.unchecked || 0;
    }
  });
  const hasFo = foTotal > 0;

  const guestComment =
    ACTIVITIES.map((activity) =>
      cleanComment(room[activity.key]?.comment || ""),
    ).find((comment) => comment && comment.trim().length > 0) || "";
  const hasGuest = guestComment.length > 0;

  const openFoNotes = () => {
    if (!hasFo || !primaryLineId) {
      return;
    }
    props.onOpenComments({
      id: primaryLineId,
      room_code: room.code,
      guest_name: primaryGuest,
    });
  };

  return (
    <CCard $glow={glowKind} $menuOpen={menuOpen}>
      <CTop>
        <CCode
          type="button"
          aria-label={`${room.code} — ${t("actions")}`}
          onClick={() => setMenuOpen(!menuOpen)}
        >
          {room.code}
          <ChevronDown size={15} aria-hidden="true" />
        </CCode>
        <CStatus $bg={pMeta.bg} $glow={room.status === "vacant_dirty"}>
          <PhysicalIcon size={13} aria-hidden="true" />
          {t(pMeta.labelKey)}
        </CStatus>
      </CTop>

      {menuOpen ? (
        <>
          <Backdrop onClick={() => setMenuOpen(false)} />
          <CPopover>
            <PopLabel>{t("change_status")}</PopLabel>
            {PHYSICAL_STATUS_KEYS.map((key) => {
              const meta = PHYSICAL_STATUS_META[key];
              const current = room.status === key;
              return (
                <PopItem
                  key={key}
                  type="button"
                  disabled={current}
                  title={current ? t("status_current_hint") : ""}
                  onClick={() => {
                    setMenuOpen(false);
                    props.onChangeStatus(room, key);
                  }}
                >
                  <Dot $color={meta.bg} />
                  {t(meta.labelKey)}
                </PopItem>
              );
            })}

            <PopLabel>{t("actions")}</PopLabel>
            <PopItem
              type="button"
              onClick={() => {
                setMenuOpen(false);
                props.onToggleBlock(room);
              }}
            >
              {room.blocked ? <LockOpen size={14} /> : <Lock size={14} />}
              {room.blocked ? t("unblock_room") : t("block_room")}
            </PopItem>
            {room.blocked ? (
              <PopItem
                type="button"
                onClick={() => {
                  setMenuOpen(false);
                  props.onEditBlockReason(room);
                }}
              >
                <NotebookPen size={14} />
                {t("edit_block_reason")}
              </PopItem>
            ) : null}
            {room.block_movable ? (
              <PopItem
                type="button"
                onClick={() => {
                  setMenuOpen(false);
                  props.onMoveBlock(room);
                }}
              >
                <MoveRight size={14} />
                {t("move_block")}
              </PopItem>
            ) : null}
            {/* Only on a MOVABLE block: block_movable already means an operator block with no
             * reservation on it, which is exactly the room a house-use stay can be written onto.
             * A close-out block or a night that already holds a booking must not offer this. */}
            {room.block_movable ? (
              <PopItem
                type="button"
                onClick={() => {
                  setMenuOpen(false);
                  props.onMarkHouseUse(room);
                }}
              >
                <HousePlus size={14} />
                {t("house_use_menu")}
              </PopItem>
            ) : null}
            <PopItem
              type="button"
              disabled={!room.reopen_eligible}
              onClick={() => {
                setMenuOpen(false);
                props.onRequestReopen(room);
              }}
            >
              <RotateCcw size={14} />
              {t("request_reopen")}
            </PopItem>
            {room.reopen_criteria ? (
              <Reasons>
                {room.reopen_eligible ? (
                  <Reason $ok>✓ {t("reopen_ready")}</Reason>
                ) : (
                  <>
                    <ReasonsTitle>{t("reopen_why")}</ReasonsTitle>
                    {REOPEN_CRIT_KEYS.filter(
                      (key) => room.reopen_criteria[key] === false,
                    ).map((key) => (
                      <Reason key={key} $ok={false}>
                        ✗ {t(`crit_${key}`)}
                      </Reason>
                    ))}
                  </>
                )}
              </Reasons>
            ) : null}
          </CPopover>
        </>
      ) : null}

      <CTypeRow>
        <CType title={room.room_type_name}>{typeLabel}</CType>
        <CPillGroup>
          {dateStatuses.map((meta) => {
            const DateIcon = meta.Icon;
            return (
              <CPill key={meta.labelKey} $c={meta.color} $solid={meta.solid}>
                <DateIcon size={11} aria-hidden="true" />
                {t(meta.labelKey)}
                {meta.suffix || ""}
              </CPill>
            );
          })}
        </CPillGroup>
      </CTypeRow>

      {/* Compact is the DEFAULT view, so a block with no visible reason here is a block nobody can
          explain at handover. */}
      {room.blocked && (room.block_reason || room.block_note) ? (
        <CBlockReason title={room.block_note || undefined}>
          {room.block_reason ? t(`block_reason_${room.block_reason}`) : null}
          {room.block_reason && room.block_note ? " — " : ""}
          {room.block_note}
        </CBlockReason>
      ) : null}

      <CNotes>
        <CNoteChip
          type="button"
          $on={hasFo}
          $accent={theme.goldDark}
          $clickable={hasFo}
          title={hasFo ? t("fo_notes_label") : t("fo_empty")}
          onClick={openFoNotes}
        >
          <NotebookPen size={13} aria-hidden="true" />
          {t("fo_notes_label")}
          {hasFo
            ? ` ${foUnchecked > 0 ? `${foUnchecked}/` : ""}${foTotal}`
            : ""}
        </CNoteChip>
        <CNoteChip
          type="button"
          $on={hasGuest}
          $accent="#b45309"
          $clickable={false}
          title={hasGuest ? guestComment : t("guest_notes_label")}
        >
          <StickyNote size={13} aria-hidden="true" />
          {t("guest_notes_label")}
        </CNoteChip>
      </CNotes>

      {acts.length > 0 ? (
        <CActs>
          {acts.map((act) => {
            const ActIcon = act.Icon;
            return (
              <CAct
                key={act.key}
                $bg={act.bg}
                $clickable={Boolean(act.lineId)}
                role={act.lineId ? "button" : undefined}
                onClick={
                  act.lineId ? () => props.onOpenBooking(act.lineId) : undefined
                }
              >
                <CActLabel $fg={act.fg}>
                  <ActIcon size={13} aria-hidden="true" />
                  {act.label}
                </CActLabel>
                <GuestCell>
                  {sourceBadge(act.sourceKey, t, 14)}
                  <CActGuest title={act.guest}>{act.guest || "—"}</CActGuest>
                  {act.houseUse ? (
                    <HouseUseMark title={t("house_use_badge")}>
                      {t("house_use_badge")}
                    </HouseUseMark>
                  ) : null}
                  {act.ratePaid === false && !act.houseUse ? (
                    <UnpaidMark
                      title={t("unpaid_mark")}
                      aria-label={t("unpaid_mark")}
                    >
                      <HandCoins size={11} aria-hidden="true" />
                    </UnpaidMark>
                  ) : null}
                  <ExtraBedBadge count={act.extraBeds} />
                </GuestCell>
                {act.lateUntil ? (
                  <CLate title={`${t("late_until")} ${act.lateUntil}`}>
                    <Clock size={11} />
                    {act.lateUntil}
                  </CLate>
                ) : null}
                {act.note ? <CCross title={act.note}>{act.note}</CCross> : null}
                {act.attention ? (
                  <CAttn title={t("email_attention_short")}>
                    <AlertTriangle size={12} />
                  </CAttn>
                ) : null}
              </CAct>
            );
          })}
        </CActs>
      ) : null}
    </CCard>
  );
});
CompactCard.displayName = "CompactCard";

const groupByBuilding = (rooms) => {
  const acc = {};
  rooms.forEach((room) => {
    const bCode = room.building_code || "-";
    const fCode = room.floor_code || "-";
    acc[bCode] = acc[bCode] || {
      name: room.building_name || bCode,
      floors: {},
    };
    acc[bCode].floors[fCode] = acc[bCode].floors[fCode] || {
      name: room.floor_name || fCode,
      rooms: [],
    };
    acc[bCode].floors[fCode].rooms.push(room);
  });
  return acc;
};

const RackIndex = (props) => {
  const { t } = useT();
  const rooms = useMemo(() => props.rooms || [], [props.rooms]);
  const date = props.date;
  // Stable ref (like `rooms` above) so a falsy notes_counts doesn't churn identity and defeat the
  // memoized RoomCard shallow compare, forcing a full-grid re-render on any RackIndex state change.
  const notesCounts = useMemo(
    () => props.notes_counts || {},
    [props.notes_counts],
  );
  const [commentsLine, setCommentsLine] = useState(null);
  const [drawerLineId, setDrawerLineId] = useState(readDrawerLineId);
  const [density, setDensity] = useState(readDensity);
  const [dialog, setDialog] = useState(null);
  const [blockRoom, setBlockRoom] = useState(null);
  const [houseUseRoom, setHouseUseRoom] = useState(null);
  const [moveBlockRoom, setMoveBlockRoom] = useState(null);
  const [reopenRoom, setReopenRoom] = useState(null);

  const chooseDensity = (value) => {
    setDensity(value);
    if (typeof localStorage !== "undefined") {
      localStorage.setItem(DENSITY_KEY, value);
    }
  };

  // Keep ?drawer=<lineId> in the URL so a reload (F5) reopens the same booking drawer.
  useDrawerUrlSync(drawerLineId);

  // Whole-page freshness: background partial reload of ALL props (rooms, statuses, pulse band, note
  // badges). preserveEqualProps keeps unchanged references, so memoized RoomCards don't re-render.
  usePoll(45_000);

  const changeStatus = useCallback(
    (room, status) => {
      router.post(
        `/frontdesk_v2/rooms/${room.id}/physical_status`,
        { status, date },
        { preserveScroll: true },
      );
    },
    [date],
  );

  const submitReopen = useCallback(
    (roomId, lineId) =>
      router.post(
        `/frontdesk_v2/rooms/${roomId}/request_reopen`,
        { line_id: lineId, date },
        { preserveScroll: true },
      ),
    [date],
  );

  const requestReopen = useCallback(
    (room) => {
      const candidates = room.reopen_candidates || [];
      if (candidates.length === 0) {
        return;
      }
      // >1 departed line to release → let the FO pick which; exactly 1 → confirm with the date.
      if (candidates.length > 1) {
        setReopenRoom(room);
        return;
      }
      const candidate = candidates[0];
      /* Say which rule is in force right now, not just what is being released. Whether this needs an
       * approver decides what the FO does next — chase a supervisor, or watch it happen — and
       * whether it hands the nights back to eZee or blocks the room is the difference between the
       * room selling again tonight and being deliberately held.
       */
      const policy = reopenPolicyText(candidate.policy, t);
      setDialog({
        title: t("confirm_generic_title"),
        body: `${t("confirm_reopen")} — ${candidate.guest_name || t("guest")}: ${candidate.effective_from} → ${candidate.check_out_date} (${candidate.nights_to_release} ${t("reopen_nights")})`,
        /* The dialog's own callout slot rather than appended to the body: Body has no
         * white-space:pre-line, so a concatenated block would collapse into one run of text, and
         * this is the part the FO has to actually read before deciding.
         */
        note: policy ? `${t("reopen_policy_heading")} ${policy}` : null,
        confirmLabel: t("confirm_ok"),
        onConfirm: () => {
          setDialog(null);
          submitReopen(room.id, candidate.line_id);
        },
        onCancel: () => setDialog(null),
      });
    },
    [submitReopen, t],
  );

  // Blocking asks WHY; unblocking has nothing to record, so it stays a plain confirmation.
  const toggleBlock = useCallback(
    (room) => {
      if (!room.blocked) {
        setBlockRoom(room);
        return;
      }
      setDialog({
        title: t("confirm_generic_title"),
        body: t("confirm_unblock"),
        confirmLabel: t("confirm_ok"),
        onConfirm: () => {
          setDialog(null);
          router.post(
            `/frontdesk_v2/rooms/${room.id}/toggle_block`,
            { date },
            { preserveScroll: true },
          );
        },
        onCancel: () => setDialog(null),
      });
    },
    [date, t],
  );

  const editBlockReason = useCallback((room) => {
    setBlockRoom({ ...room, editing: true });
  }, []);

  const moveBlock = useCallback((room) => {
    setMoveBlockRoom(room);
  }, []);

  const markHouseUse = useCallback((room) => {
    setHouseUseRoom(room);
  }, []);

  const submitHouseUse = useCallback(
    ({ occupantName, checkOutDate, note }) => {
      const room = houseUseRoom;
      setHouseUseRoom(null);
      if (!room) return;
      router.post(
        `/frontdesk_v2/rooms/${room.id}/house_use`,
        {
          date: date,
          check_out_date: checkOutDate,
          occupant_name: occupantName,
          note: note,
        },
        { preserveScroll: true },
      );
    },
    [houseUseRoom, date],
  );

  const submitBlock = useCallback(
    ({ reason, note }) => {
      const room = blockRoom;
      setBlockRoom(null);
      if (!room) return;
      // Editing must not go through toggle_block, or "fix the typo" would unblock the room.
      const path = room.editing ? "block_note" : "toggle_block";
      router.post(
        `/frontdesk_v2/rooms/${room.id}/${path}`,
        { date, block_reason: reason, block_note: note },
        { preserveScroll: true },
      );
    },
    [blockRoom, date],
  );

  const openComments = useCallback((line) => setCommentsLine(line), []);
  const openBooking = useCallback((lineId) => setDrawerLineId(lineId), []);

  const byBuilding = useMemo(() => groupByBuilding(rooms), [rooms]);

  return (
    <PageShell refreshMs={45_000}>
      <Head title={t("rack_title")} />
      <Header>
        <div>
          <Title>{t("rack_title")}</Title>
          <Subtitle>{t("rack_subtitle")}</Subtitle>
        </div>
        <ViewToggle>
          <ViewButton
            type="button"
            $active={density === "compact"}
            onClick={() => chooseDensity("compact")}
          >
            {t("view_compact")}
          </ViewButton>
          <ViewButton
            type="button"
            $active={density === "detailed"}
            onClick={() => chooseDensity("detailed")}
          >
            {t("view_detailed")}
          </ViewButton>
        </ViewToggle>
      </Header>

      {props.property_status ? (
        <PropertyStatusBar data={props.property_status} showBulkBlock />
      ) : null}

      <Legend />

      {rooms.length === 0 ? (
        <Empty>{t("empty_rooms")}</Empty>
      ) : (
        Object.keys(byBuilding).map((bCode) => (
          <Section key={bCode}>
            <SectionTitle>{byBuilding[bCode].name}</SectionTitle>
            {Object.keys(byBuilding[bCode].floors).map((fCode) => (
              <div key={fCode}>
                <FloorTitle>
                  {t("floor")} {byBuilding[bCode].floors[fCode].name}
                </FloorTitle>
                {density === "compact" ? (
                  <CompactGrid>
                    {byBuilding[bCode].floors[fCode].rooms.map((room) => (
                      <CompactCard
                        key={room.id}
                        room={room}
                        onOpenBooking={openBooking}
                        onOpenComments={openComments}
                        notesCounts={notesCounts}
                        onChangeStatus={changeStatus}
                        onToggleBlock={toggleBlock}
                        onEditBlockReason={editBlockReason}
                        onMoveBlock={moveBlock}
                        onMarkHouseUse={markHouseUse}
                        onRequestReopen={requestReopen}
                      />
                    ))}
                  </CompactGrid>
                ) : (
                  <Grid>
                    {byBuilding[bCode].floors[fCode].rooms.map((room) => (
                      <RoomCard
                        key={room.id}
                        room={room}
                        date={date}
                        onOpenBooking={openBooking}
                        notesCounts={notesCounts}
                        onChangeStatus={changeStatus}
                        onToggleBlock={toggleBlock}
                        onEditBlockReason={editBlockReason}
                        onMoveBlock={moveBlock}
                        onMarkHouseUse={markHouseUse}
                        onRequestReopen={requestReopen}
                        onOpenComments={openComments}
                      />
                    ))}
                  </Grid>
                )}
              </div>
            ))}
          </Section>
        ))
      )}

      {drawerLineId ? (
        <BookingDrawer
          key={drawerLineId}
          lineId={drawerLineId}
          date={date}
          onClose={() => setDrawerLineId(null)}
          onOpenComments={openComments}
        />
      ) : null}

      {commentsLine ? (
        <FoCommentsModal
          line={commentsLine}
          onClose={(dirty) => {
            setCommentsLine(null);
            if (dirty) {
              router.reload({ preserveScroll: true });
            }
          }}
        />
      ) : null}

      {blockRoom ? (
        <BlockRoomDialog
          roomCode={blockRoom.code}
          editing={blockRoom.editing}
          reason={blockRoom.block_reason}
          note={blockRoom.block_note}
          onConfirm={submitBlock}
          onCancel={() => setBlockRoom(null)}
        />
      ) : null}

      {houseUseRoom ? (
        <HouseUseDialog
          roomCode={houseUseRoom.code}
          date={date}
          onConfirm={submitHouseUse}
          onCancel={() => setHouseUseRoom(null)}
        />
      ) : null}

      {moveBlockRoom ? (
        <MoveBlockModal
          room={moveBlockRoom}
          date={date}
          onClose={() => setMoveBlockRoom(null)}
        />
      ) : null}

      {dialog ? (
        <ConfirmDialog
          title={dialog.title}
          body={dialog.body}
          note={dialog.note}
          confirmLabel={dialog.confirmLabel}
          onConfirm={dialog.onConfirm}
          onCancel={dialog.onCancel}
        />
      ) : null}

      {reopenRoom ? (
        <ReopenPickModal
          room={reopenRoom}
          onClose={() => setReopenRoom(null)}
          onConfirm={(lineId) => {
            const roomId = reopenRoom.id;
            setReopenRoom(null);
            submitReopen(roomId, lineId);
          }}
        />
      ) : null}
    </PageShell>
  );
};

export default RackIndex;
